import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TrendingUp, TrendingDown, BookOpen, Users, GraduationCap, Library, ArrowUpRight } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import { fetchLmsClasses } from "../redux/lmsSlice";
import { fetchLmsCourses } from "../redux/lmsSlice";
import "../lms.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);

// ---- Static / derived data ----
const ENROLLMENT_DATA = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep"],
  datasets: [
    {
      label: "Students",
      data: [1200, 1900, 2400, 2100, 3200, 4100, 5200, 6400, 7100],
      borderColor: "#5B4FE9",
      backgroundColor: "rgba(91, 79, 233, 0.08)",
      borderWidth: 2,
      pointBackgroundColor: "#5B4FE9",
      pointRadius: 4,
      tension: 0.4,
      fill: true,
    },
  ],
};

const LINE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#1A1D23",
      titleFont: { size: 12, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 12 }, color: "#9CA3AF" },
    },
    y: {
      min: 0,
      max: 8000,
      ticks: {
        stepSize: 1000,
        font: { size: 12 },
        color: "#9CA3AF",
        callback: (v) => (v >= 1000 ? `${v / 1000}k` : v),
      },
      grid: { color: "#F0F0F0" },
    },
  },
};

const DONUT_DATA = {
  labels: ["Computer Science", "Business", "Data Science", "Design", "Marketing"],
  datasets: [
    {
      data: [35, 20, 18, 15, 12],
      backgroundColor: ["#5B4FE9", "#14B8A6", "#8B5CF6", "#10B981", "#F97316"],
      borderWidth: 0,
      hoverOffset: 6,
    },
  ],
};

const DONUT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#1A1D23",
      callbacks: {
        label: (ctx) => ` ${ctx.label}: ${ctx.parsed}%`,
      },
    },
  },
};

const ACTIVITY_FEED = [
  { initials: "AS", name: "Aisha Sharma", action: "enrolled in", course: "Advanced Mathematics", time: "2 min ago", status: "Active" },
  { initials: "RK", name: "Rahul Kumar", action: "submitted assignment in", course: "Physics Fundamentals", time: "15 min ago", status: "Completed" },
  { initials: "PM", name: "Priya Mehta", action: "uploaded material for", course: "Chemistry Lab", time: "1 hr ago", status: "Needs Review" },
  { initials: "VG", name: "Vikram Gupta", action: "started course", course: "Computer Science Basics", time: "2 hr ago", status: "Active" },
  { initials: "SD", name: "Sneha Das", action: "completed quiz in", course: "English Literature", time: "3 hr ago", status: "Completed" },
];

const STATUS_STYLE = {
  Active:       { background: "#D1FAE5", color: "#065F46" },
  Completed:    { background: "#DBEAFE", color: "#1E40AF" },
  "Needs Review": { background: "#FEF3C7", color: "#92400E" },
  Pending:      { background: "#FFEDD5", color: "#9A3412" },
  Draft:        { background: "#FEF3C7", color: "#92400E" },
};

// ---- Sub-components ----
function StatCard({ icon: Icon, iconBg, iconColor, value, label, badge, badgeType = "up", footer }) {
  const isUp = badgeType === "up";
  return (
    <div className="lms-card lms-stat-card">
      <div className="lms-stat-top">
        <div className="lms-stat-icon" style={{ background: iconBg, color: iconColor }}>
          <Icon size={18} strokeWidth={2} />
        </div>
        {badge && (
          <span
            className="lms-stat-badge"
            style={
              isUp
                ? { background: "#D1FAE5", color: "#065F46" }
                : { background: "#FEF3C7", color: "#92400E" }
            }
          >
            {isUp ? "+" : ""}{badge}
          </span>
        )}
      </div>
      <div className="lms-stat-value">{value}</div>
      <div className="lms-stat-label">{label}</div>
      {footer && (
        <div className="lms-stat-footer">
          {isUp ? (
            <TrendingUp size={12} color="#10B981" />
          ) : (
            <TrendingDown size={12} color="#F59E0B" />
          )}
          <span>{footer}</span>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || { background: "#F3F4F6", color: "#6B7280" };
  return (
    <span
      style={{
        ...style,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 6,
        padding: "3px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

// ---- Main Dashboard ----
export default function LmsDashboard() {
  const dispatch = useDispatch();
  const { classes, courses, loading } = useSelector((s) => s.lms);

  useEffect(() => {
    dispatch(fetchLmsClasses());
    dispatch(fetchLmsCourses());
  }, [dispatch]);

  const totalClasses = classes.length;
  const totalCourses = courses.length;

  const donutLegend = DONUT_DATA.labels.map((label, i) => ({
    label,
    color: DONUT_DATA.datasets[0].backgroundColor[i],
    value: DONUT_DATA.datasets[0].data[i],
  }));

  return (
    <div className="lms-dashboard">
      {/* Page header */}
      <div className="lms-page-header">
        <div>
          <h1 className="lms-page-title">Admin Dashboard</h1>
          <p className="lms-page-subtitle">
            Real-time academic overview, student engagement &amp; operational metrics.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="lms-stat-grid">
        <StatCard
          icon={Library}
          iconBg="#DBEAFE"
          iconColor="#3B82F6"
          value={loading ? "—" : totalClasses}
          label="Active Classes"
          badge="12%"
          badgeType="up"
          footer="8 sessions live today"
        />
        <StatCard
          icon={Users}
          iconBg="#D1FAE5"
          iconColor="#10B981"
          value="1,284"
          label="Total Students"
          badge="8%"
          badgeType="up"
          footer="24 new this week"
        />
        <StatCard
          icon={BookOpen}
          iconBg="#FFEDD5"
          iconColor="#F97316"
          value={loading ? "—" : totalCourses}
          label="Total Courses"
          badge="5%"
          badgeType="up"
          footer="3 published this month"
        />
        <StatCard
          icon={GraduationCap}
          iconBg="#EDE9FE"
          iconColor="#8B5CF6"
          value="42"
          label="Faculty Members"
          badge="2%"
          badgeType="down"
          footer="2 on leave today"
        />
      </div>

      {/* Charts row */}
      <div className="lms-charts-row">
        {/* Line chart */}
        <div className="lms-card lms-chart-card lms-chart-line">
          <div className="lms-chart-header">
            <div>
              <div className="lms-chart-title">Student Enrollment Growth</div>
            </div>
            <div className="lms-chart-subtitle">2026 Academic Year</div>
          </div>
          <div style={{ height: 220 }}>
            <Line data={ENROLLMENT_DATA} options={LINE_OPTIONS} />
          </div>
        </div>

        {/* Donut chart */}
        <div className="lms-card lms-chart-card lms-chart-donut">
          <div className="lms-chart-header">
            <div className="lms-chart-title">Courses by Category</div>
          </div>
          <div className="lms-donut-wrap">
            <div style={{ width: 180, height: 180, flexShrink: 0 }}>
              <Doughnut data={DONUT_DATA} options={DONUT_OPTIONS} />
            </div>
            <div className="lms-donut-legend">
              {donutLegend.map((item) => (
                <div key={item.label} className="lms-donut-legend-item">
                  <span
                    className="lms-donut-dot"
                    style={{ background: item.color }}
                  />
                  <span className="lms-donut-legend-label">{item.label}</span>
                  <span className="lms-donut-legend-val">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="lms-card lms-activity-card">
        <div className="lms-section-header">
          <span className="lms-section-title">Recent Activity</span>
          <button className="lms-link-btn">
            View Full Log <ArrowUpRight size={13} style={{ verticalAlign: "middle" }} />
          </button>
        </div>
        <div>
          {ACTIVITY_FEED.map((item, idx) => (
            <div key={idx} className="lms-activity-row">
              <div className="lms-activity-avatar">{item.initials}</div>
              <div className="lms-activity-body">
                <div className="lms-activity-text">
                  <strong>{item.name}</strong> {item.action}{" "}
                  <span style={{ color: "#5B4FE9", fontWeight: 500 }}>{item.course}</span>
                </div>
                <div className="lms-activity-time">{item.time}</div>
              </div>
              <StatusBadge status={item.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
